import {Box, List, Text} from '@mantine/core';
import type React from 'react';
import type {
  summaryContentAdvice,
  summaryContentGeneral,
  summaryContentQuestion,
  summaryContentStep,
  summaryContentThemaTexts,
} from '../../../../core/repositories/reportSummaryRepository';

export const StepBox: React.FC<{
  content: summaryContentStep;
}> = ({content}) => {
  return (
    <Box className="pr-16 text-justify">
      {content.data.map((item, itemIndex) => (
        <Box key={`gradYear-${itemIndex}`}>
          <Text className="pc:text-lg text-md font-bold mb-4 text-black-1">
            {item.gradYear}年卒
          </Text>
          <List withPadding className="list-inside">
            {item.steps.map((step, stepIndex) => (
              <List.Item
                className="pc:text-md text-base mb-4 text-black-1 pr-12"
                key={`step-${stepIndex}`}
              >
                <span className="font-bold">({stepIndex + 1})</span>{' '}
                {step.stepName}
              </List.Item>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
};

export const QuestionBox: React.FC<{
  content: summaryContentQuestion;
  isSelection?: boolean;
  isSignedIn: boolean;
}> = ({content, isSelection, isSignedIn}) => {
  const displayContent = isSignedIn
    ? content.data
    : content.data
        .filter((_, index) => index === 0)
        .map((d) => {
          return {
            items: [
              {
                category: d.items[0]?.category,
                questions: [d.items[0]?.questions[0]],
              },
            ],
            step: d.step,
          };
        });

  return (
    <Box className="pr-16 text-justify">
      {displayContent.map((item, index) => (
        <Box key={index}>
          <List>
            {!isSelection && (
              <List.Item className="pc:text-lg text-md font-bold mb-4 text-black-1">
                {item.step}
              </List.Item>
            )}
            {item.items.map((category, categoryIndex) => (
              <List
                key={`category-${categoryIndex}`}
                withPadding={!isSelection}
              >
                <List.Item className="pc:text-md text-base font-bold text-black-1">
                  {category.category}
                </List.Item>
                <List withPadding className="list-disc list-inside">
                  {category.questions.map((question, questionIndex) => (
                    <List.Item
                      key={`question-${questionIndex}`}
                      className="pc:text-md text-base text-black-1 pr-12"
                    >
                      {question}
                    </List.Item>
                  ))}
                </List>
              </List>
            ))}
          </List>
        </Box>
      ))}
      {!isSignedIn && (
        <Box className="h-[130px] bg-[url('/next-image/aiEsBuilder/blur_content.png')]" />
      )}
    </Box>
  );
};

export const AdviceBox: React.FC<{
  content: summaryContentAdvice;
  isPC: boolean;
  isSignedIn: boolean;
}> = ({content, isPC, isSignedIn}) => {
  const displayContent = isSignedIn
    ? content.data
    : content.data
        .filter((_, index) => index === 0)
        .map((d) => {
          return {
            category: d.category,
            advices: [d.advices[0]],
          };
        });

  return (
    <Box className="pr-16 text-justify">
      <List>
        {displayContent.map((item, index) => (
          <List key={`category-${index}`} className="mb-16">
            <List.Item className="pc:text-lg text-md font-bold text-black-1 mb-8">
              {item.category}
            </List.Item>
            <List className="list-disc list-inside" withPadding={isPC}>
              {item.advices.map((advice, adviceIndex) => (
                <List.Item
                  key={`advice-${adviceIndex}`}
                  className="pc:text-md text-base text-black-1 pr-12 mb-8"
                >
                  {advice}
                </List.Item>
              ))}
            </List>
          </List>
        ))}
      </List>
      {!isSignedIn && (
        <Box className="h-[130px] bg-[url('/next-image/aiEsBuilder/blur_content.png')]" />
      )}
    </Box>
  );
};

export const ThemaTextsBox: React.FC<{
  content: summaryContentThemaTexts;
  isPC: boolean;
  isSignedIn: boolean;
}> = ({content, isPC, isSignedIn}) => {
  const displayContent = isSignedIn
    ? content.data
    : content.data
        .filter((_, index) => index === 0)
        .map((d) => {
          return {
            thema: d.thema,
            texts: [d.texts[0]],
          };
        });
  return (
    <Box className="pr-16 text-justify">
      <List>
        {displayContent.map((item, index) => (
          <List key={`thema-${index}`} className="mb-16">
            <List.Item className="pc:text-md text-base font-bold text-black-1 mb-8">
              {item.thema}
            </List.Item>
            <List className="list-disc list-inside" withPadding={isPC}>
              {item.texts.map((text, testIndex) => (
                <List.Item
                  key={`text-${testIndex}`}
                  className="pc:text-md text-base text-black-1 pr-12 mb-8"
                >
                  {text}
                </List.Item>
              ))}
            </List>
          </List>
        ))}
      </List>
      {!isSignedIn && (
        <Box className="h-[130px] bg-[url('/next-image/aiEsBuilder/blur_content.png')]" />
      )}
    </Box>
  );
};

export const GeneralBox: React.FC<{
  content: summaryContentGeneral;
  isPC: boolean;
  isSignedIn: boolean;
}> = ({content, isPC, isSignedIn}) => {
  const displayContent = isSignedIn
    ? content.data
    : content.data
        .filter((_, index) => index === 0)
        .map((d) => {
          return {
            answers: [d.answers[0]],
          };
        });
  return (
    <Box className="pr-16 text-justify">
      <List>
        {displayContent.map((item, index) => (
          <List
            key={`text-${index}`}
            className="mb-16 list-disc list-inside"
            withPadding={isPC}
          >
            {item.answers.map((answer, answerIndex) => (
              <List.Item
                key={`answer-${answerIndex}`}
                className="pc:text-md text-base text-black-1 pr-12 mb-8"
              >
                {answer}
              </List.Item>
            ))}
          </List>
        ))}
      </List>
      {!isSignedIn && (
        <Box className="h-[130px] bg-[url('/next-image/aiEsBuilder/blur_content.png')]" />
      )}
    </Box>
  );
};
